
import { supabase } from '@/integrations/supabase/client';

/**
 * Updates a user's streak based on their activity
 * @param userId The user's ID
 * @returns A promise that resolves to the updated streak data
 */
export const updateUserStreak = async (userId: string) => {
  // Get the current streak data
  const { data: streakData, error } = await supabase
    .from('user_streaks')
    .select('*')
    .eq('user_id', userId)
    .single();
  
  if (error) {
    // If no streak record exists, create one
    if (error.code === 'PGRST116') {
      const today = new Date().toISOString().split('T')[0];
      const { data: newStreak, error: insertError } = await supabase
        .from('user_streaks')
        .insert({
          user_id: userId,
          current_streak: 1,
          max_streak: 1,
          last_activity_date: today
        })
        .select()
        .single();
        
      if (insertError) {
        console.error('Error creating streak record:', insertError);
        throw insertError;
      }
      
      return newStreak;
    }
    
    console.error('Error fetching streak data:', error);
    throw error;
  }
  
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  // If the user already logged activity today, return existing data
  if (streakData.last_activity_date === today) {
    return streakData;
  }
  
  // Calculate if this is a consecutive day
  let isConsecutiveDay = false;
  
  if (streakData.last_activity_date) {
    const lastActivityDate = new Date(streakData.last_activity_date);
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    
    // Check if the last activity was yesterday
    const lastActivityDay = lastActivityDate.toISOString().split('T')[0];
    const yesterday = yesterdayDate.toISOString().split('T')[0];
    
    isConsecutiveDay = lastActivityDay === yesterday;
  }
  
  // Update the streak data
  let newCurrentStreak = 0;
  let newMaxStreak = streakData.max_streak;
  
  if (isConsecutiveDay) {
    // Increment the current streak if this is a consecutive day
    newCurrentStreak = streakData.current_streak + 1;
    
    // Update max streak if current streak is higher
    if (newCurrentStreak > newMaxStreak) {
      newMaxStreak = newCurrentStreak;
    }
  } else {
    // Reset the streak to 1 if this is not a consecutive day
    newCurrentStreak = 1;
  }
  
  // Update the streak in the database
  const { data: updatedStreak, error: updateError } = await supabase
    .from('user_streaks')
    .update({
      current_streak: newCurrentStreak,
      max_streak: newMaxStreak,
      last_activity_date: today
    })
    .eq('user_id', userId)
    .select()
    .single();
  
  if (updateError) {
    console.error('Error updating streak:', updateError);
    throw updateError;
  }
  
  return updatedStreak;
};
